import { LOCALE_NAMES, LOCALE_NATIVE_NAMES } from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateProfile } from "@/lib/api/profile";
import { authClient } from "@/lib/auth-client";
import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./profile-form.i18n";
import { ProfilePicture } from "./profile-picture";

type ProfileFormProps = {
  user: {
    id: string;
    name: string;
    email: string;
    image: string | null;
    emailVerified: boolean;
    isSsoUser: boolean;
    phoneNumber: string | null;
    phoneNumberVerified: boolean;
    language: string | null;
    title: string | null;
    signature: string | null;
    defaultLanguage: string;
    supportedLanguages: string[];
  };
};

export function ProfileForm({ user }: ProfileFormProps) {
  const queryClient = useQueryClient();
  const { setUserLanguage } = useUserLanguage();
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Profile form state
  const [name, setName] = useState(user.name);
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber || "");
  const [title, setTitle] = useState(user.title || "");
  const [signature, setSignature] = useState(user.signature || "");
  const [language, setLanguage] = useState(user.language || user.defaultLanguage);

  // SSO users may edit their name, but are warned once they start: the IdP owns it
  const ssoNameWarning = user.isSsoUser && name !== user.name;

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    startTransition(async () => {
      try {
        await updateProfile({
          name: name.trim(),
          phoneNumber: phoneNumber.trim() || undefined,
          title: title.trim() || undefined,
          signature: signature.trim() || undefined,
          language,
        });

        // Sync to auth session (language is saved by updateProfile above)
        await authClient.updateUser({ name: name.trim() });

        setUserLanguage(language as LocaleCode);
        setSuccess(t.profileUpdated);
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["me"] });
        queryClient.invalidateQueries({ queryKey: ["app-bootstrap"] });
      } catch (err) {
        setError(err instanceof Error ? err.message : t.failedToUpdateProfile);
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle>{t.personalInfo}</CardTitle>
          <CardDescription>{t.personalInfoDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}
            {success && (
              <div className="text-sm rounded-md border border-success/40 bg-success/10 p-3">
                {success}
              </div>
            )}

            <ProfilePicture currentImage={user.image} userName={user.name} />

            <div className="space-y-2">
              <Label htmlFor="name">{t.name}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.namePlaceholder}
                disabled={isPending}
                aria-describedby={user.isSsoUser ? "name-sso-hint" : undefined}
                className={ssoNameWarning ? "border-warning focus-visible:ring-warning/40" : ""}
              />
              {/* Always rendered for SSO users so editing only recolours it, never shifts the page */}
              {user.isSsoUser && (
                <p
                  id="name-sso-hint"
                  className={cn(
                    "flex items-start gap-1.5 text-xs transition-colors",
                    ssoNameWarning ? "text-warning" : "text-muted-foreground",
                  )}
                >
                  <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" />
                  {t.ssoNameWarning}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t.email}</Label>
              <div className="flex items-center gap-2">
                <Input id="email" value={user.email} disabled className="flex-1" />
                {user.emailVerified ? (
                  <span className="text-xs text-success whitespace-nowrap">{t.verified}</span>
                ) : (
                  <span className="text-xs text-warning whitespace-nowrap">{t.notVerified}</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phoneNumber">{t.phoneNumber}</Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder={t.phoneNumberPlaceholder}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">{t.title}</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t.titlePlaceholder}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signature">{t.signature}</Label>
              <Input
                id="signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={t.signaturePlaceholder}
                disabled={isPending}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="language">{t.language}</Label>
              <Select value={language} onValueChange={setLanguage} disabled={isPending}>
                <SelectTrigger id="language" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {user.supportedLanguages.map((locale) => (
                    <SelectItem key={locale} value={locale}>
                      {LOCALE_NAMES[locale as LocaleCode] || locale} (
                      {LOCALE_NATIVE_NAMES[locale as LocaleCode] || locale})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" loading={isPending}>
              {t.save}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
